import { useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Upload, RotateCcw } from "lucide-react";
import { Card, Btn, SaveNotice } from "@/components/kit";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useProgress, EXPORT_APP, EXPORT_MODULE } from "@/modules/premiere-video/store";

function exportData(state) {
  const payload = { app: EXPORT_APP, module: EXPORT_MODULE, version: 1, exportedAt: new Date().toISOString(), data: state };
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `ugc-go-sauvegarde-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  toast.success("Sauvegarde exportée");
}

function Confirm({ open, onOpenChange, title, desc, action, onConfirm, testId }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="rounded-[20px]" data-testid={`${testId}-dialog`}>
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display text-2xl text-bx">{title}</AlertDialogTitle>
          <AlertDialogDescription className="text-soft">{desc}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full" data-testid={`${testId}-cancel`}>Annuler</AlertDialogCancel>
          <AlertDialogAction className="rounded-full bg-bx hover:bg-bx-dark" onClick={onConfirm} data-testid={`${testId}-confirm`}>{action}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default function DataPanel() {
  const { state, replaceAll, reset } = useProgress();
  const fileRef = useRef(null);
  const [pending, setPending] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const json = JSON.parse(await file.text());
      if (json.app !== EXPORT_APP || json.module !== EXPORT_MODULE || !json.data) throw new Error();
      setPending(json.data);
    } catch {
      toast.error("Ce fichier n'est pas une sauvegarde UGC GO valide.");
    }
  };

  return (
    <Card className="no-print" data-testid="data-panel">
      <h2 className="text-2xl font-medium text-bx">Mes données</h2>
      <SaveNotice className="mt-4" />
      <div className="mt-5 flex flex-wrap gap-3">
        <Btn variant="outline" size="sm" onClick={() => exportData(state)} data-testid="export-json-button"><Download className="h-4 w-4" />Exporter mes données</Btn>
        <Btn variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="import-json-button"><Upload className="h-4 w-4" />Importer une sauvegarde</Btn>
        <Btn variant="ghost" size="sm" onClick={() => setResetOpen(true)} data-testid="reset-progress-button"><RotateCcw className="h-4 w-4" />Réinitialiser</Btn>
        <input ref={fileRef} type="file" accept="application/json,.json" className="hidden" onChange={onFile} data-testid="import-json-input" />
      </div>
      <Confirm open={!!pending} onOpenChange={(o) => !o && setPending(null)} testId="import"
        title="Importer cette sauvegarde ?" desc="Les réponses actuellement enregistrées dans ton compte seront remplacées par celles du fichier."
        action="Importer" onConfirm={() => { replaceAll(pending); setPending(null); toast.success("Sauvegarde importée"); }} />
      <Confirm open={resetOpen} onOpenChange={setResetOpen} testId="reset"
        title="Tout réinitialiser ?" desc="Toutes tes réponses et ta progression seront effacées de ton compte. Exporte une sauvegarde avant si tu veux les conserver."
        action="Oui, tout effacer" onConfirm={() => { reset(); toast("Ton parcours a été réinitialisé."); }} />
    </Card>
  );
}
